import { Navigate } from "react-router"
import type { RouteObject } from "react-router"

import { FeatureGuard } from "@/components/route/featureGuard/featureGuard"
import { RoleGuard } from "@/components/route/role-guard"
import { useAuthentication } from "@/contexts"
import { AdminPath, RootPath } from "@/models/route"
import { credentialManagerRoles } from "@/models/user-role"
import { PAGE_LAYOUT_FILL, PAGE_LAYOUT_SCROLL } from "@/utils/page-layout-mode"

import {
  AdminBillEditRoute,
  AdminBillRoute,
  AdminBillsRoute,
  AdminCredentialEditRoute,
  AdminCredentialNewRoute,
  AdminCredentialRoute,
  AdminCredentialsRoute,
  AdminMeterEditRoute,
  AdminMeterRoute,
  AdminMetersRoute,
  AdminRoute,
  AdminSiteEditRoute,
  AdminSiteRoute,
  AdminSitesRoute,
  AdminUserEditRoute,
  AdminUserNewRoute,
  AdminUserRoute,
  AdminUsersRoute,
} from "./pages"

const AdminIndexRedirect = () => {
  const { isOrgCredentialsAdminUser } = useAuthentication()

  if (isOrgCredentialsAdminUser) {
    return <Navigate to={AdminPath.Credentials} replace />
  }

  return <Navigate to={AdminPath.Sites} replace />
}

export const adminRoutes: RouteObject[] = [
  {
    path: RootPath.Admin,
    element: (
      <FeatureGuard feature="admin">
        <RoleGuard
          allowedRoles={[
            "admin",
            "admin_reader",
            "org_admin",
            "org_utility_credentials_admin",
          ]}
        >
          <AdminRoute />
        </RoleGuard>
      </FeatureGuard>
    ),
    children: [
      {
        index: true,
        element: <AdminIndexRedirect />,
      },
      {
        path: AdminPath.Bills,
        handle: { adminLayout: PAGE_LAYOUT_FILL },
        element: (
          <FeatureGuard feature="admin_bills">
            <RoleGuard allowedRoles={["admin", "admin_reader", "org_admin"]}>
              <AdminBillsRoute />
            </RoleGuard>
          </FeatureGuard>
        ),
      },
      {
        path: `${AdminPath.Bills}/${AdminPath.Bill}`,
        handle: { adminLayout: PAGE_LAYOUT_SCROLL },
        element: (
          <FeatureGuard feature="admin_bills">
            <RoleGuard allowedRoles={["admin", "admin_reader", "org_admin"]}>
              <AdminBillRoute />
            </RoleGuard>
          </FeatureGuard>
        ),
      },
      {
        path: `${AdminPath.Bills}/${AdminPath.Bill}/${AdminPath.Edit}`,
        handle: { adminLayout: PAGE_LAYOUT_SCROLL },
        element: (
          <FeatureGuard feature="admin_bills">
            <RoleGuard allowedRoles={["admin", "admin_reader", "org_admin"]}>
              <AdminBillEditRoute />
            </RoleGuard>
          </FeatureGuard>
        ),
      },
      {
        path: AdminPath.Credentials,
        handle: { adminLayout: PAGE_LAYOUT_FILL },
        element: (
          <FeatureGuard feature="admin_credentials">
            <AdminCredentialsRoute />
          </FeatureGuard>
        ),
      },
      {
        path: `${AdminPath.Credentials}/${AdminPath.New}`,
        handle: { adminLayout: PAGE_LAYOUT_SCROLL },
        element: (
          <FeatureGuard feature="admin_credentials">
            <RoleGuard allowedRoles={[...credentialManagerRoles]}>
              <AdminCredentialNewRoute />
            </RoleGuard>
          </FeatureGuard>
        ),
      },
      {
        path: `${AdminPath.Credentials}/${AdminPath.Credential}`,
        handle: { adminLayout: PAGE_LAYOUT_SCROLL },
        element: (
          <FeatureGuard feature="admin_credentials">
            <AdminCredentialRoute />
          </FeatureGuard>
        ),
      },
      {
        path: `${AdminPath.Credentials}/${AdminPath.Credential}/${AdminPath.Edit}`,
        handle: { adminLayout: PAGE_LAYOUT_SCROLL },
        element: (
          <FeatureGuard feature="admin_credentials">
            <RoleGuard allowedRoles={[...credentialManagerRoles]}>
              <AdminCredentialEditRoute />
            </RoleGuard>
          </FeatureGuard>
        ),
      },
      {
        path: AdminPath.Meters,
        handle: { adminLayout: PAGE_LAYOUT_FILL },
        element: (
          <RoleGuard allowedRoles={["admin", "admin_reader", "org_admin"]}>
            <AdminMetersRoute />
          </RoleGuard>
        ),
      },
      {
        path: `${AdminPath.Meters}/${AdminPath.Meter}`,
        handle: { adminLayout: PAGE_LAYOUT_SCROLL },
        element: (
          <RoleGuard allowedRoles={["admin", "admin_reader", "org_admin"]}>
            <AdminMeterRoute />
          </RoleGuard>
        ),
      },
      {
        path: `${AdminPath.Meters}/${AdminPath.Meter}/${AdminPath.Edit}`,
        handle: { adminLayout: PAGE_LAYOUT_SCROLL },
        element: (
          <RoleGuard allowedRoles={["admin", "admin_reader", "org_admin"]}>
            <AdminMeterEditRoute />
          </RoleGuard>
        ),
      },
      {
        path: AdminPath.Sites,
        handle: { adminLayout: PAGE_LAYOUT_FILL },
        element: (
          <RoleGuard allowedRoles={["admin", "admin_reader", "org_admin"]}>
            <AdminSitesRoute />
          </RoleGuard>
        ),
      },
      {
        path: `${AdminPath.Sites}/${AdminPath.Site}`,
        handle: { adminLayout: PAGE_LAYOUT_SCROLL },
        element: (
          <RoleGuard allowedRoles={["admin", "admin_reader", "org_admin"]}>
            <AdminSiteRoute />
          </RoleGuard>
        ),
      },
      {
        path: `${AdminPath.Sites}/${AdminPath.Site}/${AdminPath.Edit}`,
        handle: { adminLayout: PAGE_LAYOUT_SCROLL },
        element: (
          <RoleGuard allowedRoles={["admin", "admin_reader", "org_admin"]}>
            <AdminSiteEditRoute />
          </RoleGuard>
        ),
      },
      {
        path: AdminPath.Users,
        handle: { adminLayout: PAGE_LAYOUT_FILL },
        element: (
          <RoleGuard allowedRoles={["admin", "admin_reader", "org_admin"]}>
            <AdminUsersRoute />
          </RoleGuard>
        ),
      },
      {
        path: `${AdminPath.Users}/${AdminPath.New}`,
        handle: { adminLayout: PAGE_LAYOUT_SCROLL },
        element: (
          <RoleGuard allowedRoles={["admin", "org_admin"]}>
            <AdminUserNewRoute />
          </RoleGuard>
        ),
      },
      {
        path: `${AdminPath.Users}/${AdminPath.User}`,
        handle: { adminLayout: PAGE_LAYOUT_SCROLL },
        element: (
          <RoleGuard allowedRoles={["admin", "admin_reader", "org_admin"]}>
            <AdminUserRoute />
          </RoleGuard>
        ),
      },
      {
        path: `${AdminPath.Users}/${AdminPath.User}/${AdminPath.Edit}`,
        handle: { adminLayout: PAGE_LAYOUT_SCROLL },
        element: (
          <RoleGuard allowedRoles={["admin", "admin_reader", "org_admin"]}>
            <AdminUserEditRoute />
          </RoleGuard>
        ),
      },
    ],
  },
]
